import React, { Component } from 'react';
import _ from 'lodash';
import ReportHeader, { ExportType } from 'views/Analytics/AnalyticsMockService/Reporting/ReportHeader/ReportHeader';
import {
  MOCK_SERVICE_RESPONSE_TIME,
  REPORT_TRANSACTION_DAILY_COUNTS,
  TRANSACTION_DAILY_COUNTS,
  TRANSACTION_HITS_AND_MISSES,
} from 'store/analytics/analyticsReportTypes';
import { Col, Row } from 'react-bootstrap';
import { ApplicationState } from 'store/rootReducer';
import { connect } from 'react-redux';
import { MockServiceAnalytics } from 'types/rest/mockServiceTypes';
import BzmDateRangePicker, {
  DateRange,
  FixedTimeRange,
} from 'views/Analytics/AnalyticsMockService/Reporting/Pickers/BzmDateRangePicker/BzmDateRangePicker';
import Report from 'views/Analytics/AnalyticsMockService/Reporting/Report';
import {
  selectMockServices,
  selectReport,
  setDateRange,
  setFixedTimeRange,
  setMsSearchKeyword,
  setReportsConfig,
  setResolution,
} from 'store/analytics/ui/subViews/reportingView/reportingViewActions';
import { initialDateRange } from 'store/analytics/ui/subViews/reportingView/reportingViewReducer';
import { AnalyticsReport, MockServiceAnalyticsState, ReportingViewState } from 'store/analytics/ui/analyticsUiTypes';
import { ReportsData } from 'store/analytics/data/analyticsDataTypes';
import { fetchDataForReportThunk, setActiveInitialFetch } from 'store/analytics/data/analyticsDataActions';
import MixpanelService from 'utils/mixpanelService';
import { AGGREGATION_CHANGED, REFRESH_REPORTING_VIEW } from 'utils/mixpanelEvents';
import PrintableReport from 'views/Analytics/AnalyticsMockService/Reporting/PrintableReport/PrintableReport';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { downloadPdfReport, downloadPngReport } from 'utils/analyticsUtils';
import { WithTranslation, withTranslation } from 'react-i18next';

type AllProps = DispatchProps & StateProps & ReportingProps & WithTranslation;

type ReportingProps = {
  selectedMockService: MockServiceAnalytics;
};

class Reporting extends Component<AllProps> {
  state = {
    isLoadingMockServices: false,
    isExporting: false, // Controls visibility of PrintableReport component
  };

  componentDidMount() {
    // on component mount should fetch the data
    if (_.get(this.props, 'activeDataContext.reporting.initialFetch') === false) {
      this.props.fetchDataForReport(this.props.selectedMockService);
    }
  }

  refreshMockServicesHandler = async () => {
    //Refresh selection & search for now
    await this.props.selectMockServices([]);
    await this.props.setMsSearchKeyword('');

    this.setState({ isLoadingMockServices: true });
    setTimeout(() => {
      this.setState({ isLoadingMockServices: false });
    }, 1000);
  };

  dateSelectionChangedHandler = (dateRange: DateRange) => {
    this.props.setDateRange(dateRange);
    this.props.setFixedTimeRange(null);
  };

  setReportAndValidateResolution = (reportId: AnalyticsReport) => {
    this.props.selectReport(reportId);
    if (!_.isNil(this.props.activeUiContext) && !_.isNil(this.props.activeUiContext.reportingView.resolution)) {
      this.props.setResolution(this.props.activeUiContext.reportingView.resolution);
    }
  };

  refreshHandler = () => {
    MixpanelService.track(REFRESH_REPORTING_VIEW());
    this.props.fetchDataForReport(this.props.selectedMockService);
  };

  setAggregation = (report: AnalyticsReport, value: string) => {
    switch (report.id) {
      case TRANSACTION_HITS_AND_MISSES:
        MixpanelService.track(AGGREGATION_CHANGED(value));
        this.props.setResolution(value);
        break;
      case TRANSACTION_DAILY_COUNTS:
        break;
      case MOCK_SERVICE_RESPONSE_TIME:
        break;
    }
  };

  fixedRangeSelectionChangedHandler = (fixedTimeRange: FixedTimeRange) => {
    this.props.setFixedTimeRange(fixedTimeRange);
  };

  mockServiceSelectionChangedHandler = (mockServiceIds: number[]) => {
    this.props.selectMockServices(mockServiceIds);
  };

  searchKeywordChangedHandler = (keyword: string) => {
    this.props.setMsSearchKeyword(keyword);
  };

  /** Resolves IDs to virtual service objects */
  getSelectedMockServices = (msIds: number[]) => {
    const mockServices = msIds.map((id: number) => {
      if (!this.props.mockServices) {
        return;
      }
      return this.props.mockServices.find(ms => ms.id === id);
    }) as MockServiceAnalytics[];

    if (!_.isNil(this.props.selectedMockService)) {
      mockServices.unshift(this.props.selectedMockService);
    }

    return mockServices;
  };

  isDifferentReportType(previous: MockServiceAnalyticsState, current: MockServiceAnalyticsState) {
    return previous.reportingView.selectedReport.id !== current.reportingView.selectedReport.id;
  }

  isDifferentConfiguration(prevConfig: ReportingViewState, currentConfig: ReportingViewState) {
    const isDifferentTimeFrom =
      currentConfig.dateRange.from !== null &&
      prevConfig.dateRange.from !== null &&
      currentConfig.dateRange.from.toTimeString() !== new Date(prevConfig.dateRange.from).toTimeString();

    const isDifferentTimeTo =
      currentConfig.dateRange.to !== null &&
      prevConfig.dateRange.to !== null &&
      currentConfig.dateRange.to.toTimeString() !== new Date(prevConfig.dateRange.to).toTimeString();

    const isDifferentFrom =
      currentConfig.dateRange.from !== null &&
      prevConfig.dateRange.from !== null &&
      currentConfig.dateRange.from.toDateString() !== new Date(prevConfig.dateRange.from).toDateString();

    const isDifferentTo =
      currentConfig.dateRange.to !== null &&
      prevConfig.dateRange.to !== null &&
      currentConfig.dateRange.to.toDateString() !== new Date(prevConfig.dateRange.to).toDateString();

    const isDifferentFixedTimeRange = currentConfig.fixedTimeRange !== prevConfig.fixedTimeRange;

    const isDifferentResolution = currentConfig.resolution !== prevConfig.resolution;

    return (
      isDifferentFrom ||
      isDifferentTo ||
      isDifferentFixedTimeRange ||
      isDifferentResolution ||
      isDifferentTimeFrom ||
      isDifferentTimeTo
    );
  }

  componentDidUpdate(prevProps: Readonly<AllProps>): void {
    // Check if initial fetch did not happen
    if (
      this.props.activeDataContext &&
      this.props.activeUiContext &&
      this.props.activeUiContext.activeTab === 'reporting' &&
      this.props.activeDataContext.reporting.initialFetch === false
    ) {
      this.props.fetchDataForReport(this.props.selectedMockService);
    }

    //here fetch data if something in the configuration has changed (time range / virtual service selection)
    else if (
      prevProps.activeUiContext !== null &&
      this.props.activeUiContext !== null &&
      (this.isDifferentReportType(prevProps.activeUiContext, this.props.activeUiContext) ||
        this.isDifferentConfiguration(
          prevProps.activeUiContext.reportingView,
          this.props.activeUiContext.reportingView,
        ))
    ) {
      this.reloadReportData();
    }
  }

  /**
   *  Checks if reload is of data is necessary and triggers the fetch of the data again.
   */
  private reloadReportData() {
    if (this.props.activeUiContext !== null) {
      const contentState = this.props.activeUiContext;
      const prevReportConfig: ReportingViewState | undefined = _.find(
        contentState.reportsConfig.configList,
        (data: { selectedReport: AnalyticsReport }) =>
          data.selectedReport.id === contentState.reportingView.selectedReport.id,
      );
      const dataState = this.props.activeDataContext;
      let resultData: ReportsData | undefined;
      if (dataState !== null) {
        resultData = _.find(
          dataState.reporting.reportsData,
          (data: { reportType: string }) => data.reportType === contentState.reportingView.selectedReport.id,
        );
      }
      if (
        (resultData && resultData.lastReloadTs === null) ||
        (prevReportConfig !== undefined &&
          this.isDifferentConfiguration(prevReportConfig, this.props.activeUiContext.reportingView))
      ) {
        this.props.fetchDataForReport(this.props.selectedMockService);
      }
    }
  }

  getReportData = () => {
    const contentState = this.props.activeUiContext;
    const dataState = this.props.activeDataContext;
    if (contentState !== null && dataState !== null) {
      const reportData: ReportsData = _.find(
        dataState.reporting.reportsData,
        (data: { reportType: string }) => data.reportType === contentState.reportingView.selectedReport.id,
      );
      return reportData;
    }
    //Fallback empty data
    return null;
  };

  /**
   * Report function conditionally displays printable version of the report (PrintableReport component) and saves
   * it in format based on selected type (png, pdf...)
   * @param reportType
   */
  exportReport = async (reportType: ExportType) => {
    await this.setState({ isExporting: true });
    const reportName = this.getSelectedReport().name;
    this.props.showToastNotification(
      ToastType.Info,
      this.props.t('printableReport.toast.heading'),
      this.props.t('printableReport.toast.body'),
    );
    switch (reportType) {
      case 'pdfExport':
        await downloadPdfReport(reportName);
        break;
      case 'pngExport':
        await downloadPngReport(reportName);
        break;
    }
    this.setState({ isExporting: false });
  };

  /**
   * Gets currently selected report from active context
   * */
  getSelectedReport = () => {
    return this.props.activeUiContext
      ? this.props.activeUiContext.reportingView.selectedReport
      : REPORT_TRANSACTION_DAILY_COUNTS;
  };

  render() {
    const contentState = this.props.activeUiContext;
    const dateRange = contentState ? contentState.reportingView.dateRange : initialDateRange;
    const selectedMockServices = contentState ? contentState.reportingView.selectedMockServices : [];
    const selectedReport = this.getSelectedReport();

    const dataState = this.props.activeDataContext;
    const isLoadingReportData = dataState ? dataState.reporting.isLoadingData : false;

    // When from and to are loaded from localStorage, they are represented as a date string and not an object
    // we need to cast it to date so all date functions work properly
    dateRange.from = dateRange.from ? new Date(dateRange.from) : new Date();
    dateRange.to = dateRange.to ? new Date(dateRange.to) : new Date();

    const fixedTimeRange = contentState ? contentState.reportingView.fixedTimeRange : null;
    const resolution =
      contentState && contentState.reportingView.resolution ? contentState.reportingView.resolution : '1D';
    const useUTC = resolution === '1D' || resolution === '1W' || resolution === '1M';

    const reportContent = (
      <Report
        isLoadingReportData={isLoadingReportData}
        report={selectedReport}
        selectedMockServices={this.getSelectedMockServices(selectedMockServices)}
        dateRange={dateRange}
        reportData={this.getReportData()}
        resolution={resolution}
        useUTC={useUTC}
        msType={this.props.selectedMockService.type}
      />
    );

    return (
      <div className="mt-5 position-relative">
        <ReportHeader
          mockService={this.props.selectedMockService}
          isReportSelectable={true}
          onSelectReport={this.setReportAndValidateResolution}
          selectedReport={selectedReport}
          isLoading={isLoadingReportData}
          refreshAction={this.refreshHandler}
          exportAction={this.exportReport}
          setAggregation={this.setAggregation}
          selectedResolution={resolution}
        />
        <Row>
          <Col md={3}>
            {/* Calendar */}
            <BzmDateRangePicker
              dateRange={dateRange}
              fixedSelection={fixedTimeRange}
              dateRangeChanged={this.dateSelectionChangedHandler}
              fixedSelectionChanged={this.fixedRangeSelectionChangedHandler}
            />
            {/* ./Calendar */}

            {/*<hr />*/}
            {/* List of Virtual Services */}
            {/* TODO - Hidden for Phase I release */}
            {/*<MockServicePicker*/}
            {/*  selectedMockService={this.props.selectedMockService}*/}
            {/*  refreshMockServices={this.refreshMockServicesHandler}*/}
            {/*  isLoading={this.props.isLoading || this.state.isLoadingMockServices}*/}
            {/*  mockServices={this.props.mockServices}*/}
            {/*  selectedMockServices={selectedMockServices}*/}
            {/*  searchKeyWord={searchKeyword}*/}
            {/*  searchKeyWordChanged={this.searchKeywordChangedHandler}*/}
            {/*  mockServiceSelectionChanged={this.mockServiceSelectionChangedHandler}*/}
            {/*/>*/}
            {/* ./List of Virtual Services */}
          </Col>
          <Col md={9}>{reportContent}</Col>
        </Row>

        {/* Printable version of the report */}
        {this.state.isExporting && (
          <PrintableReport
            serviceName={this.props.selectedMockService ? this.props.selectedMockService.name : 'N/A'}
            reportName={selectedReport.name}
            reportDesc={this.props.t('reports.' + selectedReport.id + '.description')}
            dateRange={dateRange}
          >
            {reportContent}
          </PrintableReport>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ analytics, mockservices }: ApplicationState) => ({
  locData: mockservices.locData,
  mockServices: analytics.ui.mockServices,
  isLoading: analytics.ui.isLoading,
  activeUiContext: analytics.ui.activeUiContext,
  activeDataContext: analytics.data.activeDataContext,
});

const mapDispatchToProps = {
  selectReport,
  setDateRange,
  setFixedTimeRange,
  setResolution,
  selectMockServices,
  setMsSearchKeyword,
  setReportsConfig,
  fetchDataForReport: fetchDataForReportThunk,
  setInitialFetch: setActiveInitialFetch,
  showToastNotification,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withTranslation('analytics')(connect(mapStateToProps, mapDispatchToProps)(Reporting));
